import React from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import StatusBadge from "./StatusBadge";
import { PLAN_TYPES } from "../../../constants/jewellery.constant";
import {
  UserIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  TrendingUpIcon,
} from "@heroicons/react/outline";

const PlanCard = ({ plan }) => {
  const navigate = useNavigate();

  const getPlanTypeLabel = (type) => {
    switch (type) {
      case PLAN_TYPES.VALUE_SAVINGS:
        return "Value Savings";
      case PLAN_TYPES.WEIGHT_SAVINGS:
        return "Weight Savings";
      case PLAN_TYPES.PRICE_LOCK:
        return "Price Lock";
      default:
        return type;
    }
  };

  const getProgressPercentage = () => {
    if (!plan.templateSnapshot?.tenureMonths) return 0;
    return Math.min(
      (plan.paymentsDone / plan.templateSnapshot.tenureMonths) * 100,
      100
    );
  };

  const progressPercent = getProgressPercentage();

  return (
    <div
      onClick={() => navigate(`/jewellery/${plan._id}`)}
      className="bg-background border border-neutral-light rounded-lg p-6 hover:shadow-lg cursor-pointer transition-shadow"
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-medium text-text">{plan.planNumber}</h3>
          <p className="text-xs text-neutral-dark">
            {getPlanTypeLabel(plan.templateSnapshot?.type)}
          </p>
        </div>
        <StatusBadge status={plan.status} />
      </div>

      {/* Customer Info */}
      <div className="flex items-center mb-4">
        <UserIcon className="h-4 w-4 text-neutral-dark mr-2" />
        <div>
          <p className="text-sm font-medium text-text">
            {plan.userId?.name || "Customer"}
          </p>
          <p className="text-xs text-neutral-dark">
            {plan.userId?.phoneNumber}
          </p>
        </div>
      </div>

      {/* Plan Details */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <CurrencyRupeeIcon className="h-4 w-4 text-neutral-dark mr-2" />
            <span className="text-sm text-neutral-dark">Monthly</span>
          </div>
          <span className="text-sm font-medium text-text">
            ₹{plan.monthlyAmount?.toLocaleString()}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <CalendarIcon className="h-4 w-4 text-neutral-dark mr-2" />
            <span className="text-sm text-neutral-dark">Started</span>
          </div>
          <span className="text-sm text-text">
            {format(new Date(plan.startDate), "dd MMM yyyy")}
          </span>
        </div>

        {plan.maturesOn && (
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <TrendingUpIcon className="h-4 w-4 text-neutral-dark mr-2" />
              <span className="text-sm text-neutral-dark">Matures</span>
            </div>
            <span className="text-sm text-text">
              {format(new Date(plan.maturesOn), "dd MMM yyyy")}
            </span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-neutral-dark">Progress</span>
          <span className="text-text font-medium">
            {plan.paymentsDone || 0} /{" "}
            {plan.templateSnapshot?.tenureMonths || 0} payments
          </span>
        </div>
        <div className="w-full bg-neutral-light rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              progressPercent === 100 ? "bg-success" : "bg-primary"
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <p className="text-xs text-neutral-dark text-right">
          {progressPercent.toFixed(1)}% complete
        </p>
      </div>

      {/* Wallet Balance */}
      <div className="mt-4 pt-4 border-t border-neutral-light">
        <div className="flex justify-between items-center">
          <span className="text-sm text-neutral-dark">Wallet Balance</span>
          <span className="text-lg font-bold text-primary">
            {plan.templateSnapshot?.type === PLAN_TYPES.WEIGHT_SAVINGS
              ? `${plan.wallet?.grams || 0} gms`
              : `₹${(plan.wallet?.valueInr || 0).toLocaleString()}`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PlanCard;
